import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import JobCard from '../components/JobCard';
import JobDrawer from '../components/JobDrawer';
import { createAxiosInstance } from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import { ErrorContext } from "../context/ErrorContext";
import {
  Button,
  Typography,
  Box,
  Paper,
  Container,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  Autocomplete,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const JobList = () => {
  const [jobs, setJobs] = useState([])
  const [recommendedJobs, setRecommendedJobs] = useState([])
  const [userApplications, setUserApplications] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [categories, setCategories] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [showCategoryFilter, setShowCategoryFilter] = useState(false)
  const [categorySearchInput, setCategorySearchInput] = useState('')
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState(null)
  const [isDrawerLoading, setIsDrawerLoading] = useState(false)
  const { setError } = useContext(ErrorContext);
  const { user } = useContext(AuthContext);
  // const { getToken, isSignedIn } = useAuth();
  const [isLoading, setIsLoading] = useState(false)
  const [isRecommendedLoading, setIsRecommendedLoading] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams();

  // Create axios instance - uses localStorage token automatically
  const authAxios = useMemo(() => {
    return createAxiosInstance(null);
  }, []);

  const fetchJobs = async (categoryIds = []) => {
    try {
      setIsLoading(true)
      const params = { limit: 100 }
      if (categoryIds.length > 0) {
        params.categories = categoryIds.join(',')
      }
      const response = await authAxios.get('/job/', { params })
      const results = response.data.results
      // results.sort((a, b) => a.title.localeCompare(b.title));
      setJobs(results)
    } catch (error) {
      setError("Failed to fetch jobs")
    } finally {
      setIsLoading(false)
    }
  }

  const fetchRecommendedJobs = async () => {
    if (!user) return;
    
    try {
      setIsRecommendedLoading(true)
      const params = { limit: 8 } // Limit to 8 jobs for recommended section
      
      // Extract category names from user's resumes
      if (user.resumes && user.resumes.length > 0) {
        const categoryNames = new Set();
        user.resumes.forEach(resume => {
          if (resume.categories && Array.isArray(resume.categories)) {
            resume.categories.forEach(category => categoryNames.add(category));
          }
        });
        
        if (categoryNames.size > 0) {
          params.category_names = Array.from(categoryNames).join(',');
        }
      }
      
      const response = await authAxios.get('/job/', { params })
      const results = response.data.results
      setRecommendedJobs(results)
    } catch (error) {
      setError("Failed to fetch recommended jobs")
    } finally {
      setIsRecommendedLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      setIsCategoriesLoading(true)
      const response = await authAxios.get('/categories/')
      const data = response.data
      const allCategories = data.results || data
      setCategories(allCategories)
    } catch (error) {
      setError("Failed to fetch categories")
    } finally {
      setIsCategoriesLoading(false)
    }
  }

  const fetchUserApplications = async () => {
    if (!user) return;
    try {
      const response = await authAxios.get('/application/', { params: { limit: 100 } })
      setUserApplications(response.data.results)
    } catch (error) {
      // Don't block jobs if this fails
    }
  }

  useEffect(() => {
    fetchJobs();
    fetchCategories();
    if (user) {
      fetchRecommendedJobs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    fetchUserApplications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Check for opp_id parameter in URL on mount and when it changes
  useEffect(() => {
    const oppId = searchParams.get('opp_id');
    if (oppId) {
      setSelectedJobId(oppId);
      setDrawerOpen(true);
      setIsDrawerLoading(true);
    }
  }, [searchParams])

  // Map jobId to status for quick lookup
  const jobIdToStatus = {};
  const appliedJobIds = new Set();
  userApplications.forEach(app => {
    console.log(app);
    if (app.job_uuid) {
      jobIdToStatus[app.job_uuid] = app.status;
      appliedJobIds.add(app.job_uuid);
    }
  });

  console.log("FROM JOBLIST - List of applied jobs IDs:", Array.from(appliedJobIds));

  // Filter jobs based on search term
  const filteredJobs = useMemo(() => {
    if (!searchTerm.trim()) {
      return jobs;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return jobs.filter(job => {
      return (
        job.title?.toLowerCase().includes(searchLower) ||
        job.location?.toLowerCase().includes(searchLower) ||
        job.recruiter_organizations?.[0]?.organisation?.name?.toLowerCase().includes(searchLower) ||
        job.company?.toLowerCase().includes(searchLower) ||
        job.description?.toLowerCase().includes(searchLower) ||
        job.requirements?.toLowerCase().includes(searchLower)
      );
    });
  }, [jobs, searchTerm]);

  // Filter handlers
  const handleCategoriesToggle = () => {
    setShowCategoryFilter(!showCategoryFilter);
    setCategorySearchInput('');
  };

  const handleCategorySelection = (event, newValue) => {
    setSelectedCategories(newValue);
    const categoryIds = newValue.map(category => category.id);
    fetchJobs(categoryIds);
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    fetchJobs([]);
    setShowCategoryFilter(false);
    setCategorySearchInput('');
  };

  const handleRemoveCategory = (categoryToRemove) => {
    const newSelectedCategories = selectedCategories.filter(category => category.id !== categoryToRemove.id);
    setSelectedCategories(newSelectedCategories);
    const categoryIds = newSelectedCategories.map(category => category.id);
    fetchJobs(categoryIds);
  };

  const handleJobApply = (jobId) => {
    setSelectedJobId(jobId);
    setDrawerOpen(true);
    setIsDrawerLoading(true);
    // Update URL with opp_id parameter
    setSearchParams({ opp_id: jobId });
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedJobId(null);
    setIsDrawerLoading(false);
    // Remove opp_id parameter from URL
    setSearchParams({});
  };

  const handleDrawerLoaded = () => {
    setIsDrawerLoading(false);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: "#f9fafc",
      py: 4,
      px: 2,
      pt: 4,
      transition: 'all 0.3s ease',
      width: drawerOpen ? { xs: '100%', md: '50%' } : '100%',
      borderRight: drawerOpen ? '1px solid #e2e8f0' : 'none',
    }}>
      <Container maxWidth={drawerOpen ? "lg" : "xl"} sx={{ pt: 0, px: { xs: 2, md: 2 } }}>

        <Paper sx={{ 
          background: "#f9fafc",
          borderRadius: 0,
          boxShadow: 'none',
          backdropFilter: 'blur(10px)',
          p: 4,
          mb: 4
        }}>
          {/* Stats Section for Recruiters */}
          {/* {user?.is_recruiter && jobs.length > 0 && (
            <Box sx={{ 
              mb: 4,
              p: 3, 
              background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%)',
              borderRadius: 2,
              border: '1px solid #e3f2fd'
            }}>
              <Typography variant="h6" fontWeight={600} sx={{ color: 'primary.main', mb: 2 }}>
                Job Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main' }}>
                      {jobs.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Total Jobs
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'secondary.main' }}>
                      {jobs.filter(job => job.is_active).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Active Jobs
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'success.main' }}>
                      {jobs.reduce((total, job) => total + (job.application_count || 0), 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Total Applications
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )} */}

          {/* Recommended Opportunities Section - Only for logged in users */}
          {user && (
            <>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WorkIcon sx={{ fontSize: 32, color: '#000', mr: 2 }} />
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#000' }}>
                    Recommended Opportunities
                  </Typography>
                </Box>
              </Box>

              {/* Recommended Jobs Grid */}
              <Box sx={{ mb: 6 }}>
                {isRecommendedLoading ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%)',
                    borderRadius: 2,
                    border: '1px solid #e3f2fd'
                  }}>
                    <CircularProgress size={32} sx={{ color: 'primary.main', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Loading recommendations...
                    </Typography>
                  </Box>
                ) : recommendedJobs && recommendedJobs.length > 0 ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: drawerOpen ? {
                        xs: '1fr',
                        sm: '1fr',
                        md: '1fr',
                        lg: '1fr'
                      } : {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(3, 1fr)'
                      },
                      gap: 3,
                      width: '100%'
                    }}
                  >
                    {recommendedJobs.filter(Boolean).map((job, idx) => (
                      <Box key={`rec-${idx}`} sx={{ width: '100%', minWidth: 0 }}>
                        <JobCard job={job} status={jobIdToStatus[job.uuid]} onApplyClick={handleJobApply} isDrawerOpen={drawerOpen} />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%)',
                    borderRadius: 2,
                    border: '1px solid #e3f2fd'
                  }}>
                    <WorkIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1, opacity: 0.6 }} />
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      No recommendations available at the moment
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Header Section */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 4,
            pb: 3,
            borderBottom: '2px solid #f0f0f0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WorkIcon sx={{ fontSize: 32, color: '#000', mr: 2 }} />
              <Typography variant="h4" fontWeight={700} sx={{ color: '#000' }}>
                Available Opportunities
              </Typography>
            </Box>
            {/* {user?.is_recruiter && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => window.location.href = '/post-job/'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                Post New Job
              </Button>
            )} */}
          </Box>

          {/* Search and Filter Section */}
          <Box sx={{ mb: 4 }}>
            {/* Search Bar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search jobs by position, location, company, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '16px 14px',
                    fontSize: '16px',
                  },
                }}
              />
              
              {/* Categories Button */}
              <Button
                variant="outlined"
                onClick={handleCategoriesToggle}
                startIcon={<FilterListIcon />}
                sx={{
                  minWidth: 140,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: showCategoryFilter || selectedCategories.length > 0 ? 'primary.main' : 'transparent',
                  color: showCategoryFilter || selectedCategories.length > 0 ? 'white' : 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: showCategoryFilter || selectedCategories.length > 0 ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                    borderColor: 'primary.main',
                  },
                }}
              >
                Categories {selectedCategories.length > 0 && `(${selectedCategories.length})`}
              </Button>
            </Box>

            {/* Selected Categories Display */}
            {selectedCategories.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                p: 2, 
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                borderRadius: 2,
                border: '1px solid rgba(25, 118, 210, 0.12)',
                mb: 2
              }}>
                <Typography variant="body2" sx={{ 
                  color: 'primary.main', 
                  fontWeight: 600, 
                  alignSelf: 'center',
                  mr: 1 
                }}>
                  Filtered by:
                </Typography>
                {selectedCategories.map(category => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    onDelete={() => handleRemoveCategory(category)}
                    deleteIcon={<ClearIcon />}
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'white',
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Category Filter Section */}
            {showCategoryFilter && (
              <Box sx={{
                mt: 3,
                p: 3,
                backgroundColor: 'rgba(25, 118, 210, 0.02)',
                border: '1px solid rgba(25, 118, 210, 0.12)',
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  <FilterListIcon sx={{ mr: 1 }} />
                  Select Categories
                </Typography>
                
                {isCategoriesLoading ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    py: 4 
                  }}>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Loading categories...
                    </Typography>
                  </Box>
                ) : categories.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    py: 4 
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No categories available
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Category Selection Autocomplete */}
                    <Autocomplete
                      multiple
                      id="category-filter"
                      options={categories}
                      getOptionLabel={(option) => option.name}
                      value={selectedCategories}
                      onChange={handleCategorySelection}
                      inputValue={categorySearchInput}
                      onInputChange={(event, newInputValue) => setCategorySearchInput(newInputValue)}
                      noOptionsText="No matching categories"
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.name}
                            sx={{
                              backgroundColor: 'primary.main',
                              color: 'white',
                              fontWeight: 500,
                              '& .MuiChip-deleteIcon': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                  color: 'white',
                                }
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Start typing to see list"
                          variant="outlined"
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'white',
                            }
                          }}
                        />
                      )}
                      sx={{ mb: 3 }}
                    />
                    
                    <Divider sx={{ mb: 3 }} />
                    
                    {/* Action Buttons */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      justifyContent: 'flex-end',
                      flexWrap: 'wrap'
                    }}>
                      <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        sx={{
                          minWidth: 120,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 3,
                        }}
                      >
                        Clear All
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Search Results Count */}
            {searchTerm && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} found for "{searchTerm}"
              </Typography>
            )}
            
            {/* Filter Results Count */}
            {selectedCategories.length > 0 && !searchTerm && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {jobs.length} job{jobs.length !== 1 ? 's' : ''} found in selected categories
              </Typography>
            )}
          </Box>

          {/* Jobs List */}
          <Box sx={{ mt: 3 }}>
            {isLoading ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%)',
                borderRadius: 2,
                border: '1px solid #e3f2fd'
              }}>
                <CircularProgress size={48} sx={{ color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  Loading Opportunities
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  Please wait while we fetch the latest openings...
                </Typography>
              </Box>
            ) : filteredJobs && filteredJobs.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%)',
                borderRadius: 2,
                border: '1px solid #e3f2fd'
              }}>
                <WorkIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.6 }} />
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  {searchTerm ? 'No Opportunities Found' : 'No Active Openings'}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  {searchTerm ? 'Try adjusting your search terms' : 'Check back later for new opportunities'}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: drawerOpen ? {
                    xs: '1fr',
                    sm: '1fr',
                    md: '1fr',
                    lg: '1fr'
                  } : {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(3, 1fr)'
                  },
                  gap: 3,
                  width: '100%'
                }}
              >
                {filteredJobs.filter(Boolean).map((job, idx) => (
                  <Box key={idx} sx={{ width: '100%', minWidth: 0 }}>
                    <JobCard job={job} status={jobIdToStatus[job.uuid]} onApplyClick={handleJobApply} isDrawerOpen={drawerOpen} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Job Details Panel */}
      <JobDrawer 
        open={drawerOpen} 
        onClose={handleDrawerClose} 
        jobId={selectedJobId}
        isLoading={isDrawerLoading}
        onJobLoaded={handleDrawerLoaded}
        appliedJobIds={appliedJobIds}
      />
    </Box>
  );
};

export default JobList;
